import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { RestaurantService } from '../restaurant.service';
import { TraceTransportService } from './trace-transport.service';
import { OrderFlowSessionService } from './order-flow-session.service';
import { redact } from './trace-redactor';
import { ORDER_FLOW_STEPS, StepSpec } from './order-flow-steps';
import { hiveTs, sampleBucket, inSample } from './trace-utils';
import { ORDER_FLOW_COHORT_IDS, ORDER_FLOW_INCIDENT_IDS } from './order-flow-cohort-ids';

/**
 * OrderFlowTraceService
 *
 * Emitter dos traces do funil do pedido → stack SAM order-flow-monitor.
 * Registra o INPUT e o OUTPUT das funções e telas que produzem um pedido.
 *
 * Responde "com quais dados", que é o que o CloudwatchMetricsService (só
 * contadores, Value: 1.0) nunca conseguiu responder — e é o que permite gerar
 * fixture a partir do que produção de fato faz, em vez do que alguém digitou em
 * 2019.
 *
 * O mecanismo (transporte, gates de ambiente, ts Hive, amostragem) vem de
 * telemetry/trace-transport + trace-utils, compartilhados com o
 * GeocodeTraceService. Aqui mora só o que é do funil: coorte, versão de cohort,
 * redação e correlação.
 *
 * Fire-and-forget, sempre: nada aqui pode derrubar nem atrasar o checkout.
 */

/**
 * Bump a cada PR de instrumentação; sufixo -obs-vN para sub-iterações.
 *
 * `v4-ng18` marcou a entrega do upgrade de framework (Angular 9→18 / Ionic 5→8);
 * `v5-sentry` marcou a entrega do Sentry + terceiros fora do boot (MR !1771);
 * `v6-quero-esse` marca a instrumentação da foto ampliada do item e do botão
 * "Quero esse" (ClientItemPhoto*), mais o `opened_via` do ClientProductOpened —
 * e é ele que separa "device com a build sem os steps novos" de "botão que
 * ninguém usa": sem o corte por versão, ausência de evento é ambígua.
 * O bump aqui é o que torna o DELTA calculável: `client_code_version` é dimensão
 * do rollup (`funnel_15m` agrupa por ela, ver a view `v_funnel_15m`), então as
 * duas builds convivendo em produção viram duas linhas comparáveis na MESMA
 * janela de 15 min — comparação pareada, imune à sazonalidade de horário/dia.
 * Sem o bump, o único separador seria o tempo, que mente: SPA já carregada em
 * cache segue emitindo com o código antigo por horas depois do deploy.
 *
 * CHARSET: só [a-z0-9-], e o valor tem de ser a PRIMEIRA ocorrência do padrão
 * neste arquivo. O job `order_flow_deploy_annotation` do CI extrai a versão do
 * FONTE por `grep -o` (classe [a-z0-9-]) + `head -1`, então:
 *  - um `+` ou `_` no valor seria truncado em silêncio;
 *  - escrever o PADRÃO literal num comentário acima desta linha faz o `head -1`
 *    casar o comentário e enviar a versão truncada (acontecido: a anotação saiu
 *    como a raiz sem o sufixo). Descreva o padrão, nunca o reproduza aqui.
 * Em ambos os casos a anotação de release divergiria do que o app de fato manda.
 */
export const CLIENT_CODE_VERSION = 'order-flow-v6-quero-esse';

export type CohortMode = 'restaurant' | 'sample' | 'hybrid' | 'all' | 'off';

export interface Cohort {
  mode: CohortMode;
  restaurantIds?: number[];
  samplePct?: number;
}

/**
 * Coorte crescente. Const no fonte de propósito: mudar de fase vira um diff
 * revisável, com bump do client_code_version junto, e o Athena separa as
 * coortes por versão.
 *
 * Fase 1 (feita): uma loja (Zezinho, 14), para validar schema e custo real por
 *                 pedido sem risco.
 * Fase 2 (feita): ~4% da base = 525 restaurantes CURADOS por diversidade
 *                 (`ORDER_FLOW_COHORT_IDS`) em vez de amostra aleatória — cobre
 *                 layout, fluxo de endereço, frete, pagamento, canais, país,
 *                 volume, as 44 lojas de pizza LEGADO e a camada de anomalias.
 *                 A loja 14 (âncora) está na lista. Ver aws-sam-order-flow-
 *                 monitor/cohort/ para o gerador, o CSV e o relatório.
 * Fase 2.5 (esta): HÍBRIDA = curada ∪ amostra global de devices. As 525 lojas
 *                 curadas seguem com 100% dos devices (densidade para golden
 *                 tests); TODAS as demais entram com samplePct% dos devices —
 *                 cobertura de plataforma NÃO-enviesada para o monitor de
 *                 saúde (rollup 15-min/alertas por fluxo), que precisa
 *                 enxergar caminhos de código fora da curadoria. Amostra por
 *                 DEVICE (nunca por evento): a tentativa inteira entra ou sai
 *                 junta, senão as razões de conversão do funil quebram.
 * Fase 3:         { mode: 'all' }
 *
 * Só subir samplePct depois de revisar volume × custo no dia seguinte
 * (AWS-CUSTO-GUARDRAILS.md do repo da stack; backstop diário de US$18 no S3).
 */
export const ORDER_FLOW_COHORT: Cohort = {
  mode: 'hybrid',
  // A curada (gerada) mais as lojas de incidente aberto, que precisam de 100% dos
  // devices para a medição ter potência. Ver ORDER_FLOW_INCIDENT_IDS.
  restaurantIds: [...ORDER_FLOW_COHORT_IDS, ...ORDER_FLOW_INCIDENT_IDS],
  samplePct: 10,
};

@Injectable({ providedIn: 'root' })
export class OrderFlowTraceService {
  constructor(
    private restaurantService: RestaurantService,
    private session: OrderFlowSessionService,
    private transport: TraceTransportService,
  ) { }

  private restaurantId(): number {
    const info = this.restaurantService.restaurant && this.restaurantService.restaurant.info;
    return Number(info && info.id) || 0;
  }

  /** O restaurante está no layout novo? Sem isto não dá para separar as duas populações. */
  private layout(): string {
    try {
      const info: any = this.restaurantService.restaurant && this.restaurantService.restaurant.info;
      const modules = (info && info.modules) || {};
      return modules.new_flow_checkout_pedir_delivery ? 'novo' : 'legado';
    } catch (e) {
      return 'desconhecido';
    }
  }

  /**
   * "Fingerprint" de cenário: a config do restaurante que DEFINE a qual estrato
   * da coorte este funil pertence (modelo de pizza, método de frete, provider de
   * pagamento, moeda/país, fluxo de endereço, canal, cupom). Sem isto os 525 são
   * INFATIÁVEIS no Athena — a config vive no banco, fora da conta AWS —, então
   * vai em todo evento, como o `layout`, para cada linha ser auto-descritiva sem
   * exigir self-join por funil. Tudo lido defensivamente: campo ausente vira
   * null/false e nunca lança (telemetria é fire-and-forget).
   */
  private scenario(): any {
    try {
      const info: any = this.restaurantService.restaurant && this.restaurantService.restaurant.info;
      if (!info) return null;
      const m: any = info.modules || {};
      const photonR = !!info.use_photon;      // restaurants.use_photon — o que o app lê
      const photonM = !!m.use_photon;         // restaurant_modules.use_photon — pode divergir
      return {
        pizza_model: m.new_nep_pizzas ? 'nep' : 'legado',
        fee_method: info.delivery_fee_method || null,
        payment_provider: info.payment_provider || null,
        currency: info.currency || null,
        country: info.country || null,
        use_photon: photonR,
        use_photon_module: photonM,
        photon_mismatch: photonR !== photonM,   // a esquisitice que o app ramifica
        self_service: !!m.table_self_service,
        has_tables: !info.no_table,
        coupons_active: !!info.coupoms_active,
      };
    } catch (e) {
      return null;
    }
  }

  /** Gate de coorte, aplicado por DEVICE — nunca por evento (ver trace-utils.inSample). */
  private inCohort(rid: number): boolean {
    const c = ORDER_FLOW_COHORT;
    if (c.mode === 'off') return false;
    if (c.mode === 'all') return true;
    if (c.mode === 'restaurant') return (c.restaurantIds || []).indexOf(rid) !== -1;
    if (c.mode === 'sample') return inSample(this.session.deviceId, c.samplePct || 0);
    // União: loja curada (100% dos devices) OU device na amostra global.
    // Mantém a densidade da coorte para os goldens e adiciona cobertura de
    // plataforma para o monitor — sem viés de curadoria no agregado.
    if (c.mode === 'hybrid') {
      return (c.restaurantIds || []).indexOf(rid) !== -1
        || inSample(this.session.deviceId, c.samplePct || 0);
    }
    return false;
  }

  /**
   * Gate dos eventos de SAÚDE: com restaurante carregado, o gate normal; sem
   * (rid=0, erro de bootstrap), só o gate por device serve — um app que quebra
   * ANTES de carregar a loja é exatamente o incidente que não pode ficar cego.
   */
  private inHealthCohort(rid: number): boolean {
    if (rid) return this.inCohort(rid);
    const c = ORDER_FLOW_COHORT;
    if (c.mode === 'off') return false;
    if (c.mode === 'all') return true;
    return inSample(this.session.deviceId, c.samplePct || 0);
  }

  /**
   * Amostragem POR EVENTO, só para steps de alta frequência (clique de +/-).
   * Determinística por (device, chave): o mesmo device sempre reporta os mesmos
   * steps, então a coorte não fica com buracos aleatórios.
   *
   * A chave é o nome do step, salvo quando o step declara `sampleKey` — steps
   * que formam razão (numerador/denominador) compartilham a chave para entrar e
   * sair da amostra JUNTOS. Ver StepSpec.sampleKey.
   */
  private passesStepSample(step: string): boolean {
    const spec: StepSpec | undefined = ORDER_FLOW_STEPS[step];
    const s = spec ? spec.sample : undefined;
    if (s === undefined || s >= 1) return true;
    const key = (spec && spec.sampleKey) || step;
    return inSample(this.session.deviceId + ':' + key, Math.round(s * 100));
  }

  /**
   * @param step   nome do evento — precisa existir em ORDER_FLOW_STEPS, senão o
   *               redator descarta o payload inteiro (default-deny).
   * @param io     `{ in, out }` da função; telas usam só `out`.
   * @param caller literal da call-site — o único campo que sobrevive à minificação.
   * @param extra  `{ ms, err, orderUuid, orderId }`.
   */
  emit(
    step: string,
    io: { in?: any; out?: any } = {},
    caller = '',
    extra: { ms?: number; err?: string; orderUuid?: string; orderId?: any } = {},
  ): void {
    this.emitInternal(step, io, caller, extra, false);
  }

  /**
   * Emissão de SAÚDE (ClientAppError / ClientApiCallFailed).
   *
   * Difere do emit() em dois pontos, ambos deliberados:
   *  - gate: aceita rid=0 (erro de BOOTSTRAP, antes do restaurante carregar,
   *    não pode ser invisível) — nesse caso o gate é só por device;
   *  - transporte: sendBeacon (sobrevive a unload/crash de navegação, que é
   *    quando o erro mais importa), com fallback para o POST normal.
   */
  emitHealth(step: string, out: any, caller = ''): void {
    this.emitInternal(step, { out }, caller, {}, true);
  }

  private emitInternal(
    step: string,
    io: { in?: any; out?: any },
    caller: string,
    extra: { ms?: number; err?: string; orderUuid?: string; orderId?: any },
    isHealth: boolean,
  ): void {
    try {
      // Sem endpoint configurado, não há telemetria. É o gate que separa os
      // ambientes: green/blue/beta têm `production: true` (então o guard de
      // ambiente NÃO os pega) e apontam para backends próprios — se emitissem,
      // poluiriam o funil de produção com tráfego de teste.
      if (!environment.orderFlowLogURL) return;
      if (!this.transport.canEmit()) return;

      const rid = this.restaurantId();
      if (isHealth) {
        if (!this.inHealthCohort(rid)) return;
      } else {
        if (!rid || !this.inCohort(rid)) return;
      }
      if (!this.passesStepSample(step)) return;

      // Step fora da taxonomia não tem allowlist, e sem allowlist o redator
      // descarta tudo (default-deny): errar o nome do step não vaza dado.
      const spec: StepSpec | undefined = ORDER_FLOW_STEPS[step];
      const inR = redact(io.in, spec ? spec.in : undefined);
      const outR = redact(io.out, spec ? spec.out : undefined);

      // Abre o attempt ANTES de montar o objeto.
      //
      // Não é estilo: o literal abaixo é avaliado na ordem em que está escrito,
      // então `correlation_id: this.session.attemptId` leria '' se o attempt só
      // nascesse no `nextSeq()` da linha seguinte — e o PRIMEIRO evento de cada
      // funil (o ClientMenuViewed, que abre a trilha) sairia órfão, sem chave de
      // junção. É o mesmo bug que o `ensureId` do AddressCaptureSessionService
      // foi criado para corrigir no dataset de geocode; aqui ele foi pego pelo
      // E2E, que acusou o funil partido em dois correlation_ids.
      const correlationId = this.session.ensureAttempt();
      const funnelSeq = this.session.nextSeq();

      const data: any = {
        in: inR.value,
        out: outR.value,
        // Sinal de allowlist desatualizada: campo que o app manda e ninguém
        // declarou. -1 significa payload patológico (circular/getter que lança).
        _dropped: inR.dropped + outR.dropped,
        // Correlação — o que torna o funil reconstruível.
        correlation_id: correlationId,
        funnel_seq: funnelSeq,
        cart_id: this.session.cartId,
        device_id: this.session.deviceId,
        sample_bucket: sampleBucket(this.session.deviceId),
        // Elo com o dataset de geocode: um campo, e o JOIN no Athena fica trivial.
        address_correlation_id: this.session.addressCorrelationId,
        address_flow: this.session.addressFlow,
        layout: this.layout(),
        // Config que define o estrato da coorte — em todo evento, para fatiar os 525.
        scenario: this.scenario(),
        triggered_via_caller_explicit: caller,
        user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '').slice(0, 200),
        client_code_version: CLIENT_CODE_VERSION,
      };

      if (extra.ms !== undefined) data.ms = Math.round(extra.ms);
      if (extra.err !== undefined) data.err = extra.err;

      const envelope = {
        ts: hiveTs(),
        source: 'pedirdelivery-client',
        step,
        order_uuid: extra.orderUuid || '',
        // string, não bigint: é '0' antes do commit, e o Firehose rejeitaria a
        // mistura de tipos no mesmo campo Parquet.
        order_id: String(extra.orderId || '0'),
        restaurant_id: String(rid),
        data: JSON.stringify(data),
      };

      if (isHealth) {
        this.transport.sendBeacon(environment.orderFlowLogURL, envelope);
      } else {
        this.transport.send(environment.orderFlowLogURL, envelope);
      }
    } catch (e) {
      // Telemetria nunca pode derrubar o checkout — o erro morre aqui.
      //
      // Mas morrer em silêncio também em DEV/E2E esconderia um emitter quebrado
      // até alguém estranhar o dataset vazio semanas depois. Fora de produção,
      // o erro aparece no console (e no log do Cypress).
      if (!environment.production || this.transport.isCypressRun()) {
        // eslint-disable-next-line no-console
        console.error('[order-flow] emit falhou —', step, e);
      }
    }
  }

  /** Encerra o funil: pedido enviado ou carrinho esvaziado. */
  endFunnel(): void {
    try {
      this.session.clear();
    } catch (e) {
      // idem
    }
  }
}
